import { SeverityType } from 'src/types/OtherTypes';

//* Token expiration utilities ----------------------------------
export const isTokenExpired = (expiryDate: string): boolean => {
  if (!expiryDate) return true;

  try {
    // Parse the ISO8601 date string (e.g., "2025-09-30T08:43:24Z" or "2025-09-30T08:43:24.000Z")
    const expiryDateObj = new Date(expiryDate);

    // Check if the date is valid
    if (isNaN(expiryDateObj.getTime())) {
      console.error('Invalid date format:', expiryDate);
      return true;
    }

    const now = new Date();
    return now >= expiryDateObj;
  } catch (error) {
    console.error('Error parsing token expiry date:', error);
    return true; // If we can't parse the date, consider it expired
  }
};

//* Check if access token needs refresh (with buffer time)
export const shouldRefreshToken = (accessTokenExpiry: string, refreshTokenExpiry: string): boolean => {
  if (!accessTokenExpiry) return true;

  try {
    // Parse the ISO8601 access token expiry date
    const accessTokenExpiryDate = new Date(accessTokenExpiry);

    // Check if the date is valid
    if (isNaN(accessTokenExpiryDate.getTime())) {
      console.error('Invalid access token expiry date format:', accessTokenExpiry);
      return true;
    }

    // Add 5 minute buffer before actual expiry
    const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
    const now = new Date();

    return (accessTokenExpiryDate.getTime() - now.getTime()) <= bufferTime;
  } catch (error) {
    console.error('Error checking if token should be refreshed:', error);
    return true;
  }
};

//* Check if user has valid authentication tokens
export const hasValidAuthTokens = (): boolean => {
  // Import getAuthTokens dynamically to avoid circular dependency
  const accessToken = window.localStorage.getItem('access');
  const refreshToken = window.localStorage.getItem('refresh');
  const accessTokenExpiry = window.localStorage.getItem('accessTokenExpiry');
  const refreshTokenExpiry = window.localStorage.getItem('refreshTokenExpiry');

  // If no tokens at all, user is not authenticated
  if (!accessToken && !refreshToken) return false;

  // If access token is expired and no valid refresh token, user is not authenticated
  if (isTokenExpired(accessTokenExpiry) && (!refreshTokenExpiry || isTokenExpired(refreshTokenExpiry))) {
    return false;
  }

  return true;
};

//* This function extracts exception_value from django error html page
export const ExtractErrorMessage = (htmlString: any) => {
  const parser: any = new DOMParser();
  const doc: any = parser.parseFromString(htmlString, 'text/html');
  const preElement: any = doc.querySelector('pre.exception_value');

  if (preElement) {
    return preElement?.textContent.trim();
  }

  return 'Error 404';
};

//* ---------------------------------------------------
export const urlToProperWords = (value: any) => {
  let name = value;
  name = name.split('?')[0];
  name = name.replaceAll('/', ' ');
  name = name.replace(/\b\w/g, (char: string) => char.toUpperCase());
  return name;
};

//* Enter fullscreen ----------------------------------
export const enterFullscreen = () => {
  const element = document.documentElement;
  if (element.requestFullscreen) {
    element.requestFullscreen();
  } else if ((element as any).webkitRequestFullscreen) {
    (element as any).webkitRequestFullscreen();
  } else if ((element as any).msRequestFullscreen) {
    (element as any).msRequestFullscreen();
  }
};

//* Exit fullscreen ----------------------------------
export const exitFullscreen = () => {
  if (document.exitFullscreen) {
    document.exitFullscreen();
  } else if ((document as any).webkitExitFullscreen) {
    (document as any).webkitExitFullscreen();
  } else if ((document as any).msExitFullscreen) {
    (document as any).msExitFullscreen();
  }
};

//* Parse Json String ----------------------------------
export const parseJsonString = (str: string) => {
  try {
    return JSON.parse(str);
  } catch (e) {
    return str;
  }
};

//* Export To Excel ----------------------------------
export const exportToExcel = async ({ data, fileName = 'export', sheetName = 'Sheet1', filename_datetime = true }: any) => {
  // //! Target package is 'xlsx'
  if (!data || data.length === 0) {
    // console.warn('No data available to export.');
    alert('No data available');
    return;
  }
  // Dynamically import XLSX only when needed
  const XLSX = await import('xlsx');
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  if (filename_datetime) {
    const formattedDate = new Date().toLocaleDateString().replace(/\./g, '-');
    XLSX.writeFile(wb, `${fileName}-${formattedDate}.xlsx`);
  } else {
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  }
};

/* export async function exportToExcel_0({
  data,
  fileName = 'export',
  sheetName = 'Sheet1',
  filename_datetime = true,
  callback,
}: {
  data: any[];
  fileName?: string;
  sheetName?: string;
  filename_datetime?: boolean;
  callback?: (sheet: any) => void;
}) {
  //! Target package is 'json-as-xlsx' || realized it is already dependent on xlsx.

  if (!data || data.length === 0) {
    console.warn('No data available to export.');
    return;
  }

  // Dynamically import json-as-xlsx only when needed
  const { default: exportXlsx } = await import('json-as-xlsx');

  // Prepare the final filename
  const finalFileName = filename_datetime ? `${fileName}-${new Date().toLocaleDateString().replace(/\./g, '-')}` : fileName;

  // Auto-generate columns if not provided
  const columns =
    data.length > 0
      ? Object.keys(data[0]).map((key) => ({
        label: key,
        value: key, // Simple 1:1 mapping by default
      }))
      : [];

  // Prepare data in json-as-xlsx format
  const exportData = [
    {
      sheet: sheetName,
      columns,
      content: data,
    },
  ];

  // Export settings
  const settings = {
    fileName: finalFileName,
    extraLength: 3, // Adds some padding for styling
    writeMode: 'writeFile', // Default behavior (downloads file)
    writeOptions: {}, // Optional SheetJS write options
    RTL: false, // Right-to-left display (false by default)
  };

  // Trigger export with optional callback
  exportXlsx(exportData, settings, callback);
} */

//* Get Latest Version ----------------------------------
export function getLatestVersion(changelogdata: any[]) {
  // INPUT: [{ version: '0.9.9' }, { version: '1.0.0-beta.0' }] | OUTPUT: 1.0.0-beta.0
  const CL = changelogdata;

  if (!CL || !Array.isArray(CL) || CL.length === 0) {
    throw new Error('Invalid or empty changelog provided.');
  }

  return CL.map((entry) => entry.version)
    .filter((version) => version && version !== 'unreleased')
    .sort((a, b) => compareVersions(a, b))
    .pop();
}

//* Get Latest Version Pre-Release Name ----------------------------------
export function getLatestVersionPreRelease(changelogdata: any[]): string | null {
  // INPUT: 1.0.0-beta.0 | OUTPUT: beta
  const latestVersion = getLatestVersion(changelogdata);

  if (!latestVersion) return null;

  // Extract pre-release name from version string
  const match = latestVersion.match(/^(\d+)\.(\d+)\.(\d+)(?:-(alpha|beta|rc)\.(\d+))?$/);

  if (match && match[4]) {
    // Return the pre-release type (alpha, beta, rc)
    return match[4];
  }

  return null;
}

//* Get Latest Version Pre-Release Version ----------------------------------
export function getLatestVersionPreReleaseVersion(changelogdata: any[]): string | null {
  // INPUT: 1.0.0-beta.0 | OUTPUT: 0
  const latestVersion = getLatestVersion(changelogdata);

  if (!latestVersion) return null;

  // Extract pre-release version from version string
  const match = latestVersion.match(/^(\d+)\.(\d+)\.(\d+)(?:-(alpha|beta|rc)\.(\d+))?$/);

  if (match && match[5]) {
    // Return the pre-release version (0, 1, 2, ...)
    return match[5];
  }

  return null;
}

//* Get Latest Version Simple (without pre-release) ----------------------------------
export function getLatestVersionSimple(changelogdata: any[]): string | null {
  // INPUT: 1.0.0-beta.0 | OUTPUT: 1.0.0
  const latestVersion = getLatestVersion(changelogdata);

  if (!latestVersion) return null;

  // Extract just the semantic version (MAJOR.MINOR.PATCH) without pre-release
  const match = latestVersion.match(/^(\d+)\.(\d+)\.(\d+)(?:-(alpha|beta|rc)\.(\d+))?$/);

  if (match) {
    // Return just the MAJOR.MINOR.PATCH part
    return `${match[1]}.${match[2]}.${match[3]}`;
  }

  return latestVersion; // Fallback to original version if regex doesn't match
}

// Semantic version comparison
function compareVersions(a: string, b: string): number {
  // Handle pre-release versions (alpha, beta, rc)
  const parseVersion = (version: string) => {
    const match = version.match(/^(\d+)\.(\d+)\.(\d+)(?:-(alpha|beta|rc)\.(\d+))?$/);
    if (!match) return null;

    return {
      major: parseInt(match[1]),
      minor: parseInt(match[2]),
      patch: parseInt(match[3]),
      preRelease: match[4] || null,
      preReleaseNumber: match[5] ? parseInt(match[5]) : null
    };
  };

  const versionA = parseVersion(a);
  const versionB = parseVersion(b);

  if (!versionA || !versionB) {
    // Fallback to string comparison if parsing fails
    return a.localeCompare(b);
  }

  // Compare major, minor, patch
  if (versionA.major !== versionB.major) return versionA.major - versionB.major;
  if (versionA.minor !== versionB.minor) return versionA.minor - versionB.minor;
  if (versionA.patch !== versionB.patch) return versionA.patch - versionB.patch;

  // Handle pre-release versions
  // Stable versions (no pre-release) are considered greater than pre-release versions
  if (!versionA.preRelease && versionB.preRelease) return 1;
  if (versionA.preRelease && !versionB.preRelease) return -1;

  // If both are pre-release, compare pre-release type and number
  if (versionA.preRelease && versionB.preRelease) {
    const preReleaseOrder = { alpha: 1, beta: 2, rc: 3 };
    const orderA = preReleaseOrder[versionA.preRelease as keyof typeof preReleaseOrder] || 0;
    const orderB = preReleaseOrder[versionB.preRelease as keyof typeof preReleaseOrder] || 0;

    if (orderA !== orderB) return orderA - orderB;

    // Same pre-release type, compare numbers
    const numA = versionA.preReleaseNumber || 0;
    const numB = versionB.preReleaseNumber || 0;
    return numA - numB;
  }

  return 0;
}

//* Get Version Type Color ----------------------------------
export function getVersionTypeColor(typename: string) {
  switch (typename) {
    case 'bugfix':
      return 'error';
    case 'enhancement':
      return 'info';
    case 'chore':
      return undefined;
    case 'feature':
      return 'success';
    default:
      return 'primary';
  }
}

//* Give id to array elements
export function setIdAttributes(data: any[]) {
  if (!data?.length) return [];
  return data?.map((item, index) => ({
    ...item,
    id: index,
  }));
}

//* Deep flatten object with array support
export function deepFlatten(obj, parentKey = '', result = {}) {
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const newKey = parentKey ? `${parentKey}.${key}` : key;

      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        deepFlatten(obj[key], newKey, result);
      } else if (Array.isArray(obj[key])) {
        obj[key].forEach((item, index) => {
          if (typeof item === 'object' && item !== null) {
            deepFlatten(item, `${newKey}[${index}]`, result);
          } else {
            result[`${newKey}[${index}]`] = item;
          }
        });
      } else {
        result[newKey] = obj[key];
      }
    }
  }
  return result;
}

//* Format domain string by removing protocols and paths
export function formatDomainString(domain: string) {
  if (!domain) return '';
  domain = domain.trim().replace('https://www.', '').replace('http://www.', '').replace('https://', '').replace('http://', '');

  if (domain.endsWith('/')) {
    domain = domain.slice(0, -1);
  }

  const separators = ['/', '#', '?'];
  let firstSeparatorIndex = -1;
  for (const separator of separators) {
    const index = domain.indexOf(separator);
    if (index !== -1 && (firstSeparatorIndex === -1 || index < firstSeparatorIndex)) {
      firstSeparatorIndex = index;
    }
  }
  if (firstSeparatorIndex !== -1) {
    domain = domain.slice(0, firstSeparatorIndex);
  }
  return domain;
}

//* Get severity color based on level
export function getSeverityColor(severity: string) {
  switch (severity?.toLowerCase()) {
    case 'high':
      return 'error';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
    default:
      return 'primary';
  }
}

//* Email validation
export const validateEmail = (value: string): boolean => {
  if (!value || typeof value !== 'string') return false;

  //? Trim and check length constraints (RFC 3696)
  const trimmed = value.trim();
  if (trimmed.length > 254) return false; // Max email length per RFC

  //? Check for basic structure
  const parts = trimmed.split('@');
  if (parts.length !== 2) return false;

  const [localPart, domainPart] = parts;

  //? Validate local part (before @)
  if (!localPart || localPart.length > 64) return false;
  if (/[^\w.!#$%&'*+/=?^`{|}~-]/.test(localPart)) return false;
  if (localPart.startsWith('.') || localPart.endsWith('.')) return false;
  if (localPart.includes('..')) return false;

  //? Validate domain part (after @)
  if (!domainPart || domainPart.length > 253) return false;
  if (/[^a-zA-Z0-9.-]/.test(domainPart)) return false;
  if (domainPart.startsWith('-') || domainPart.endsWith('-')) return false;
  if (domainPart.startsWith('.') || domainPart.endsWith('.')) return false;
  if (domainPart.includes('..')) return false;

  //? Check for at least one dot in domain and valid TLD
  const domainParts = domainPart.split('.');
  if (domainParts.length < 2) return false;

  const tld = domainParts[domainParts.length - 1];
  if (!tld || tld.length < 2 || /[^a-zA-Z]/.test(tld)) return false;

  //? Check for common typos
  if (domainPart.includes('.con')) return false;
  if (domainPart.includes('.cpm')) return false;

  //? Optional: Check for disposable email domains if needed
  //? const disposableDomains = ['mailinator.com', 'tempmail.com', ...];
  //? if (disposableDomains.includes(domainPart.toLowerCase())) return false;

  return true;
};
//* Domain validation
export const validateUrl = (url: string): boolean => {
  //? Regex for matching a valid URL (with or without protocol and "www")
  const urlPattern = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+(\/[^\s]*)?(\?[^\s]*)?(#[^\s]*)?$/i;

  //? Check if the URL matches the pattern
  if (!urlPattern.test(url)) {
    return false; // Invalid URL
  }

  //? Additional check for valid domain structure
  const domainPart = url.replace(/^(https?:\/\/)?(www\.)?/i, '').split('/')[0];
  const domainParts = domainPart.split('.');

  //? Ensure there are at least two parts (e.g., "example.com")
  if (domainParts.length < 2) {
    return false; // Invalid domain
  }

  //? Ensure each part of the domain is valid
  for (const part of domainParts) {
    if (!/^[a-zA-Z0-9-]+$/.test(part)) {
      return false; // Invalid characters in domain part
    }
  }

  return true; // Valid URL
};
//* Returns string from error objects
export const extractErrorMsg = (errResponse: any, includeKeys: boolean = false): string => {
  //^ Case 0: error is an HTML | Extracts exception_value from django error html page 
  if (
    errResponse &&
    typeof errResponse?.response?.data === 'string' &&
    errResponse?.response?.data.includes('<html') &&
    errResponse?.response?.data.includes('exception_value')
  ) {
    const parser: any = new DOMParser();
    const doc: any = parser.parseFromString(errResponse?.response?.data, 'text/html');
    const preElement: any = doc.querySelector('pre.exception_value');
    if (preElement) {
      return preElement?.textContent.trim();
    }
  }

  // Prefer errorData from errResponse?.response?.data, fallback to errResponse?.data, fallback to errResponse
  const errorData = errResponse?.response?.data ?? errResponse?.data ?? errResponse;

  //^ Case 1: error is a string => "Something went wrong"
  if (typeof errorData === 'string') {
    return errorData;
  }

  //^ Case 2: error is an object with an 'errors' key (common API pattern)
  if (
    errorData &&
    typeof errorData === 'object' &&
    errorData.errors &&
    typeof errorData.errors === 'object'
  ) {
    // If errors is an object with arrays as values
    const values = Object.values(errorData.errors)
      .flatMap((val) => (Array.isArray(val) ? val : [val]))
      .filter(Boolean);
    if (values.length > 0) {
      return values.join(', ');
    }
  }

  //^ Case 3: error is an object where all values are arrays (Django REST Framework style)
  if (
    errorData &&
    typeof errorData === 'object' &&
    Object.values(errorData).every((v) => Array.isArray(v))
  ) {
    const messages = Object.entries(errorData)
      .map(([field, value]) => {
        const message = Array.isArray(value) ? value.join(', ') : value;
        return includeKeys ? `${field}: ${message}` : `${message}`;
      })
      .join(', ');
    return messages;
  }

  //^ Case 4: error is an object with arrays as values (but not all values are arrays)
  if (
    errorData &&
    typeof errorData === 'object' &&
    Object.entries(errorData).length > 0
  ) {
    // If any value is an array, join its messages
    const messages = Object.entries(errorData)
      .map(([field, value]) => {
        if (Array.isArray(value)) {
          return includeKeys ? `${field}: ${value.join(', ')}` : value.join(', ');
        } else if (typeof value === 'string') {
          return includeKeys ? `${field}: ${value}` : value;
        } else if (typeof value === 'object' && value !== null) {
          // Recursively extract from nested object
          return extractErrorMsg(value, includeKeys);
        }
        return '';
      })
      .filter(Boolean)
      .join(', ');
    if (messages) return messages;
  }

  //^ Case 5: error is an array of objects => "errors": [ { "field": "Invalid input" }, ... ]
  if (Array.isArray(errorData) && errorData.length > 0) {
    const messages = errorData
      .map((errObj) => {
        if (typeof errObj === 'string') return errObj;
        if (typeof errObj === 'object' && errObj !== null) {
          const [field, message] = Object.entries(errObj)[0];
          return includeKeys ? `${field}: ${message}` : `${message}`;
        }
        return '';
      })
      .filter(Boolean)
      .join(', ');
    if (messages) return messages;
  }

  //^ Case 6: error is an object with a string value => { "error": "Something went wrong" }
  if (
    errorData &&
    typeof errorData === 'object' &&
    Object.keys(errorData).length > 0
  ) {
    const firstKey = Object.keys(errorData)[0];
    const firstValue = errorData[firstKey];
    if (typeof firstValue === 'string') {
      return firstValue;
    }
    if (Array.isArray(firstValue)) {
      return firstValue.join(', ');
    }
    if (typeof firstValue === 'object' && firstValue !== null) {
      // Recursively extract from nested object
      return extractErrorMsg(firstValue, includeKeys);
    }
  }

  return '';
};

export const Get_SeverityColor_Findings = (severityName: SeverityType, theProp: string): string => {
  const severityColors = {
    critical: {
      light: '#E10E0E1A',
      dark: '#B71C1C',
    },
    high: {
      light: '#FF3B301A',
      dark: '#FF5722',
    },
    medium: {
      light: '#F9D36F1A',
      dark: '#F9A825',
    },
    information: {
      light: '#e1e8fe',
      dark: '#6085ff',
    },
    low: {
      light: '#6FD0F91A',
      dark: '#03A9F4',
    },
    default: {
      light: '#e4e4e4',
      dark: '#2b2b2b',
    },
  };

  return severityColors?.[severityName]?.[theProp] || severityColors?.default?.[theProp] || '#dedede';
};

//* Order array by severity priority (Critical > High > Medium > Information > Low)
export function orderBySeverityPriority<T extends { severity: string }>(arr: T[]): T[] {
  const priority = ["Critical", "High", "Medium", "Information", "Low"];
  return arr.slice().sort((a, b) => {
    const aIndex = priority.indexOf(a.severity);
    const bIndex = priority.indexOf(b.severity);
    // If not found, put at the end
    return (aIndex === -1 ? priority.length : aIndex) - (bIndex === -1 ? priority.length : bIndex);
  });
}

/**
 * Converts a scan name (from scan_type_map_list) to a keyname string as specified.
 * @param {string} scanName - The scan name from scan_type_map_list (e.g., 'DNS Integrity Check')
 * @returns {string} The mapped keyname string, or an empty string if not found.
 */
export function convert_scan_label_to_name(scanName: string): string {
  // Mapping from scan name to keyname string
  const mapping: Record<string, string> = {
    'Resource Takeover Defense': 'SubdomainTakeover',
    'Vulnerability Detection': 'Cve',
    'Port Scanner': 'Port',
    'DNS Integrity Check': 'Dns',
    'SSL/TLS Configuration Audit': 'Ssl',
    'Security Headers Validator': 'Header',
    'Subdomain Port Scanner': 'SubdomainPort',
    'Subdomain SSL/TLS Configuration Audit': 'SubdomainSsl',
    'Security Subdomain Headers Validator': 'SubdomainHeader',
    'Secrets & Sensitive Data Finder': 'SecretApi',
    'Malicious Content Detection': 'MaliciousResource',
    'Hidden Paths & File Detection': 'SensitiveResource',
    'Subdomain Secrets & Sensitive Data Finder': 'SubdomainSecretApi',
    'Subdomain Malicious Content Detection': 'SubdomainMaliciousResource',
    'Subdomain Hidden Paths & File Detection': 'SubdomainSensitiveResource',
    'WAF Detection': 'Waf',
    'Subdomain WAF Detection': 'SubdomainWaf',
  };

  // Return the mapped string or an empty string if not found
  return mapping[scanName] || '';
}

export const capitilizeText = (str: string): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const UTC_datetime_from_scan_history = (historyDetails: any) => {
  const date = historyDetails?.date; // 2025-07-17
  const hour = historyDetails?.hour; // 15:30
  // To UTC datetime
  const datetime = new Date(`${date}T${hour}:00Z`);
  return datetime;
}

/**
 * Decodes a JWT token and returns the payload
 * @param {string} token - The JWT token to decode
 * @returns {object|null} The decoded payload or null if invalid
 */
export const decodeJWT = (token: string): any => {
  try {
    if (!token || typeof token !== 'string') {
      return null;
    }

    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    // Add padding if needed for base64 decoding
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
    const decodedPayload = atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/'));

    return JSON.parse(decodedPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

/**
 * Extracts role from JWT token
 * @param {string} token - The JWT token
 * @returns {string|null} The role or null if not found
 */
export const extractRoleFromJWT = (token: string): string | null => {
  const payload = decodeJWT(token);
  if (!payload) {
    return null;
  }

  // Check for common role field names
  const roleFields = ['role'];
  for (const field of roleFields) {
    if (payload[field]) {
      return payload[field];
    }
  }

  return null;
};

export function isUserRoleAdmin(role: string | string[]) {
  if (!role || role === '[]' || Array.isArray(role) && role?.length === 0) return false;
  return (
    role &&
    ((typeof role === 'string' && role?.toLowerCase() === 'admin') ||
      (Array.isArray(role) && role?.some((i: string) => i?.toLowerCase() === 'admin')))
  );
}

/**
 * Formats status name for badge display
 * @param {string} status - The status string to format
 * @returns {string} Formatted status string
 */
export function formatStatusForBadge(status: string): string {
  if (!status) return '';

  // If status is "Unknown", return empty string
  if (status === 'Unknown') return '';

  // If status contains "Risk", remove it and return the rest
  if (status.includes('Risk')) {
    return status.replace('Risk', '').trim();
  }

  // Otherwise, return the status as is
  return status;
}